import { Flex } from '@assets/component/UI/Flex/Flex';
import { useStore } from '@assets/model/Store';
import { removeUrlQueryParams } from '@assets/utils/Formatting';
import { useNavUtils } from '@assets/utils/NavUtils';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import moment from 'moment';
import { DealStatusBadge } from '../DealStatusBadge/DealStatusBadge';
import * as style from './DealThumbnail.module.less';

interface IDealThumbnailProps {
    name: string;
}

const PLACEHOLDER = '- - -';

// Per QA: never hide metadata — show a placeholder until we have the value, so
// the tile structure stays consistent. Returns the placeholder for empty/null
// or the data layer's own missing tokens.
const orPlaceholder = (value: string | null | undefined): string => {
    if (value == null) return PLACEHOLDER;
    const v = value.toString().trim();
    if (v === '' || v === 'N/A' || v === '- - -' || v === '---') {
        return PLACEHOLDER;
    }
    return v;
};

const isUrl = (value: string): boolean => {
    const v = value.toLowerCase();
    return v.startsWith('http') || v.startsWith('www');
};

export const DealThumbnail = observer((p: IDealThumbnailProps) => {
    const store = useStore();
    const navUtils = useNavUtils();

    const query = store.createDataAccess(p.name);
    const companyName = query.companyName;
    const companyDesc = query.value('description');

    const website = orPlaceholder(removeUrlQueryParams(query.value('website')));
    const industry = orPlaceholder(query.value('industry'));

    const createdEpoch = query.value('report_creation_epoch');
    const createdLabel =
        orPlaceholder(createdEpoch) !== PLACEHOLDER
            ? moment.unix(Number(createdEpoch)).format('MM/DD/YY')
            : PLACEHOLDER;

    const pipelineStatus = store.getDealPipelineStatus(p.name);

    return (
        <div
            className={style.thumb}
            onClick={() => {
                navUtils.toReport(p.name);
            }}
        >
            <div className={style.cardInner}>
                <div className={style.topArea}>
                    <Flex justify="between" alignItems="center">
                        <DealStatusBadge status={pipelineStatus} />
                        <div className={style.createdLabel}>
                            Created: {createdLabel}
                        </div>
                    </Flex>
                    <div>
                        <Flex fillCell={true}>
                            <div className={style.title}>{companyName}</div>
                            <div style={{ flex: 1 }}></div>
                        </Flex>
                    </div>
                    <div className={style.desc}>{companyDesc}</div>
                </div>

                <div className={style.divider}></div>

                <Flex justify="between" className={style.metaRow}>
                    <div className={style.fieldContainer}>
                        <div className={style.fieldTitle}>URL</div>
                        {website !== PLACEHOLDER && isUrl(website) ? (
                            <a
                                href={website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={style.website}
                                onClick={e => e.stopPropagation()}
                            >
                                {website}
                            </a>
                        ) : (
                            <div className={style.fieldValue}>{website}</div>
                        )}
                    </div>
                    <div
                        className={classNames(
                            style.fieldContainer,
                            style.fieldRight
                        )}
                    >
                        <div className={style.fieldTitle}>Industry</div>
                        <div className={style.fieldValue}>{industry}</div>
                    </div>
                </Flex>
            </div>
        </div>
    );
});
